/**
 * /d/:token — the friend-duel landing.
 *
 * The token carries the tier, the game mode and the duel id; the client does
 * not hold the seed and must not. So this screen collects nothing but a tap:
 * `queue { kind:'friend', shareToken }` and the gateway resolves everything
 * else from the signed claim.
 *
 * THERE IS NOTHING TO CHOOSE HERE, AND THERE USED TO BE.
 *
 * This screen offered tier chips, defended as "a landing pad for a link so
 * stale the server cannot resolve it". That was wrong twice over. The gateway
 * always overruled them, so the control was decorative — and a control that
 * looks decisive and is not is how a player ends up in a game they did not
 * pick. That exact failure happened one layer up: a stale link on the home
 * screen produced a Countries match for somebody who had selected Cities.
 *
 * The tier does not need a fallback anyway, because the link can simply be
 * READ. The payload is signed, not encrypted, so the invitation can state what
 * it is for. `readInvite` is display only and trusted for nothing.
 *
 * The tap matters for a second reason: joining on load would race the socket
 * against a page that may still be restoring from bfcache, and `navigator.share`
 * targets open links in odd states. A deliberate gesture is more reliable.
 */

import { HANDLE_MAX_LENGTH } from '@geogame/shared';

import { navigate } from '../lib/router';
import { tierLabel } from '../lib/format';
import { readInvite } from '../lib/invite';
import { useGame } from '../store/gameStore';

const MODE_NOUN: Record<string, string> = {
  country: 'Find the Country',
  city: 'Pin the City',
  state: 'Pin the State',
};

export function ShareLandingScreen({ token }: { readonly token: string }): React.JSX.Element {
  const startMatch = useGame((s) => s.startMatch);
  const storedTier = useGame((s) => s.tier);
  const lastError = useGame((s) => s.lastError);
  const handle = useGame((s) => s.handle);
  const setHandle = useGame((s) => s.setHandle);

  const invite = readInvite(token);
  // The link's tier when it has one. The stored tier is only a label for a
  // link too old to carry one; the gateway still decides.
  const tier = invite.tier ?? storedTier;
  const group = invite.capacity > 2;

  return (
    <main className="gg-screen gg-center">
      <div className="gg-invite">
        <p className="gg-subtitle">You have been challenged</p>
        <h1 className="gg-title">{group ? 'A group is waiting' : 'A duel is waiting'}</h1>

        {/*
          The game, stated rather than offered. Same round, same clock, same
          ladder as the player who sent it — which is the whole point of a link.
        */}
        <p className="gg-invite-facts">
          {MODE_NOUN[invite.gameMode] ?? 'Find the Country'} · {tierLabel(tier)}
          {group ? ` · up to ${invite.capacity} players` : ''}
        </p>
        <p className="gg-note">
          {group
            ? 'Whoever sent this chose the game. Wait in the lobby and anyone there can start it.'
            : 'Whoever sent this chose the game. You will both play the same rounds on the same clock.'}
        </p>

        {/*
          NAME YOURSELF BEFORE YOU WALK IN.
          ==================================================================
          For an invited player this is the first screen of the game they ever
          see. They had never been offered a name -- that lived on the home
          screen, which arriving on a link skips entirely -- so they landed in
          a roster of friends as `PolarFjord417`, and a table of anonymous
          handles is the one thing a lobby exists to prevent.
          
          Set HERE rather than only in the lobby because the name typed here
          rides `hello`, so the roster is right the first time it is drawn
          rather than correcting itself a moment after everybody has looked.
          It stays editable in the lobby too, for whoever reads ahead.
        */}
        <label className="gg-invite-name">
          <span className="gg-invite-name-label">Your name</span>
          <input
            className="gg-input"
            value={handle}
            maxLength={HANDLE_MAX_LENGTH}
            placeholder="Leave blank and we'll name you"
            onChange={(e) => setHandle(e.target.value)}
            aria-label="Your name"
          />
        </label>

        {lastError ? <p className="gg-note gg-note-error">{lastError.message}</p> : null}

        <button
          type="button"
          className="gg-btn gg-btn-primary gg-btn-xl mt-4"
          onClick={() => startMatch({ mode: 'friend', tier, shareToken: token })}
        >
          {group ? 'Join the group' : 'Accept the duel'}
        </button>
        <button type="button" className="gg-btn mt-2" onClick={() => navigate({ name: 'home' })}>
          Not now
        </button>
      </div>
    </main>
  );
}
